/**
 * /hospitality/revenue-centers — the property's outlet catalog.
 *
 * Every place revenue is captured (rooms, gaming, F&B, retail, spa…),
 * mapped to the source system (PMS / POS / casino) that feeds the
 * night audit. Read-only reference list.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, Panel } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/revenue-centers")({
  component: RevenueCentersPage,
});

interface RevenueCenter {
  code: string;
  name: string;
  department: string;
  source_system: string;
}

function RevenueCentersPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-revenue-centers"],
    queryFn: () => api<RevenueCenter[]>("/api/hospitality/revenue-centers"),
  });
  const rows = q.data ?? [];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader title="Revenue Centers" subtitle="Outlets — rooms, gaming, F&B, retail" />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        ) : (
          <Panel label="Outlets">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="text-left py-1.5 pr-3">Code</th>
                  <th className="text-left py-1.5 px-3">Name</th>
                  <th className="text-left py-1.5 px-3">Department</th>
                  <th className="text-left py-1.5 pl-3">Source System</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-slate-500 dark:text-slate-400">
                      No revenue centers configured yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.code} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1.5 pr-3 font-mono text-xs font-semibold">{r.code}</td>
                    <td className="py-1.5 px-3 text-slate-800 dark:text-slate-200">{r.name}</td>
                    <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">{r.department}</td>
                    <td className="py-1.5 pl-3 text-slate-600 dark:text-slate-300">{r.source_system}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </div>
    </div>
  );
}
